import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { CaretDown, TrendUp, TrendDown, Warning, Download, FloppyDisk, X, BookmarkSimple, Trash, PaperPlaneTilt, Robot, Spinner, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReportDrillInModal from "@/components/ReportDrillInModal";
import Sparkline from "@/components/Sparkline";
import { PRESETS, TREND_PRESETS, COMPARE_PRESETS, presetLabel, rangeFor, priorRange, compareRangeFor, downloadCsv } from "@/lib/reportHelpers";
import { money } from "@/lib/money";

const short = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return money(v);
};

const GROUPS = [
  { key: "client", label: "Client" },
  { key: "project", label: "Project" },
  { key: "worker", label: "Worker" },
];
const METRICS = [
  { key: "billed", label: "Billed $", fmt: money, tone: "" },
  { key: "paid", label: "Paid $", fmt: money, tone: "text-emerald-600" },
  { key: "outstanding", label: "Outstanding $", fmt: money, tone: "text-rose-600" },
  { key: "bill", label: "Timesheet Bill $", fmt: money },
  { key: "pay", label: "Worker Pay $", fmt: money, tone: "text-emerald-600" },
  { key: "margin", label: "Margin $", fmt: money, tone: "text-emerald-600" },
  { key: "hours", label: "Hours", fmt: (n) => Number(n || 0).toLocaleString() },
  { key: "timesheet_count", label: "Timesheet #", fmt: (n) => Number(n || 0).toLocaleString() },
];

const PALETTE = ["#0f766e", "#f59e0b", "#0284c7", "#a855f7", "#f43f5e", "#64748b"];

// ---------- Data Assistant component ----------

const EXAMPLE_QUESTIONS = [
  "How many hours did all workers log in July 2026?",
  "What did we bill in Q1 2026?",
  "Which workers have active assignments right now?",
  "How many invoices are still unpaid?",
  "What is the total outstanding balance across all clients?",
];

function DataAssistant() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]); // [{question, answer, explanation}]
  const answerRef = useRef(null);
  const inputRef = useRef(null);

  const submit = async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;
    setLoading(true);
    setAnswer("");
    setExplanation("");
    setError("");

    let fullAnswer = "";
    let fullExplanation = "";

    try {
      const response = await fetch(`${API_BASE}/reports/ai-query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question: text }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.type === "explanation") { fullExplanation = evt.text; setExplanation(evt.text); }
          if (evt.type === "token") { fullAnswer += evt.text; setAnswer(fullAnswer); }
          if (evt.type === "error") { setError(evt.text); setLoading(false); return; }
          if (evt.type === "done") break;
        }
      }
      // Archive to history
      setHistory((h) => [{ question: text, answer: fullAnswer, explanation: fullExplanation }, ...h.slice(0, 4)]);
      setQuestion("");
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Scroll answer into view as it streams
  useEffect(() => {
    if (answer && answerRef.current) {
      answerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [answer]);

  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Robot size={22} weight="duotone" className="text-teal-600" />
        <h2 className="text-xl font-black text-slate-900">Data Assistant</h2>
        <span className="text-xs font-semibold bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">AI</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Ask any question about hours, billing, invoices, or workers in plain English.
      </p>

      {/* Input */}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          data-testid="ai-query-input"
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder='e.g. "How much did we bill in Q1 2026?"'
          disabled={loading}
          className="flex-1 h-11 px-4 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:opacity-60"
        />
        <button
          data-testid="ai-query-submit"
          onClick={() => submit()}
          disabled={loading || !question.trim()}
          className="h-11 px-5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
        >
          {loading
            ? <><Spinner size={16} className="animate-spin" /> Thinking…</>
            : <><PaperPlaneTilt size={16} weight="bold" /> Ask</>}
        </button>
      </div>

      {/* Example chips */}
      <div className="flex flex-wrap gap-2 mt-3">
        {EXAMPLE_QUESTIONS.map((eq) => (
          <button
            key={eq}
            onClick={() => { setQuestion(eq); submit(eq); }}
            disabled={loading}
            className="text-xs px-3 py-1 bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 rounded-full border border-slate-200 hover:border-teal-300 transition-colors disabled:opacity-50"
          >
            {eq}
          </button>
        ))}
      </div>

      {/* Answer area */}
      {(loading || answer || error) && (
        <div ref={answerRef} className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading && !answer && (
            <div className="px-5 py-6 flex items-center gap-3 text-slate-500 text-sm">
              <Spinner size={18} className="animate-spin text-teal-500" />
              Querying your data…
            </div>
          )}
          {error && (
            <div className="px-5 py-4 text-red-700 text-sm flex items-start gap-2">
              <Warning size={18} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          {answer && (
            <>
              <div className="px-5 py-4 text-slate-900 text-sm leading-relaxed whitespace-pre-wrap">
                {answer}
                {loading && <span className="inline-block w-1.5 h-4 bg-teal-500 animate-pulse ml-1 rounded-sm" />}
              </div>
              {explanation && (
                <div className="px-5 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500 flex items-center gap-1.5">
                  <ClockCounterClockwise size={12} weight="bold" />
                  <span><b>Searched:</b> {explanation}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* History — always show below current answer when available */}
      {history.length > 0 && !loading && (
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent questions</div>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <button
                  className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center justify-between"
                  onClick={() => { setAnswer(h.answer); setExplanation(h.explanation); setError(""); }}
                >
                  <span>{h.question}</span>
                  <span className="text-xs text-teal-600 font-normal ml-2">View</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- Root component ----------

export default function Reports() {
  // ---- Per-section presets ----
  const [billedPreset, setBilledPreset] = useState("ttm");
  const [billedCustom, setBilledCustom] = useState(null);
  const [paidPreset, setPaidPreset] = useState("ttm");
  const [paidCustom, setPaidCustom] = useState(null);
  const [concPreset, setConcPreset] = useState("ttm");
  const [concCustom, setConcCustom] = useState(null);
  const [dtpPreset, setDtpPreset] = useState("all");
  const [dtpCustom, setDtpCustom] = useState(null);

  // ---- Pivot-section state ----
  const [pivotPreset, setPivotPreset] = useState("ytd");
  const [pivotCustom, setPivotCustom] = useState(null);
  const [group, setGroup] = useState("client");
  const [metric, setMetric] = useState("billed");
  const [compareOn, setCompareOn] = useState(false);
  const [comparePreset, setComparePreset] = useState("prior_period");
  const [compareCustom, setCompareCustom] = useState(null);
  const [trendPreset, setTrendPreset] = useState("ttm");

  // ---- Data ----
  const [pivot, setPivot] = useState(null);
  const [pivotCompare, setPivotCompare] = useState(null);
  const [concentration, setConcentration] = useState(null);
  const [daysToPay, setDaysToPay] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [trend, setTrend] = useState(null);
  const [aging, setAging] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [reliability, setReliability] = useState(null);

  // ---- KPI cards ----
  const [billedKpi, setBilledKpi] = useState(null);
  const [billedKpiPrior, setBilledKpiPrior] = useState(null);
  const [paidKpi, setPaidKpi] = useState(null);
  const [paidKpiPrior, setPaidKpiPrior] = useState(null);

  // ---- Saved views ----
  const [savedViews, setSavedViews] = useState([]);

  // ---- Drill-in ----
  const [drill, setDrill] = useState(null);

  const pivotRange = rangeFor(pivotPreset, pivotCustom);
  const billedRange = rangeFor(billedPreset, billedCustom);
  const paidRange = rangeFor(paidPreset, paidCustom);
  const concRange = rangeFor(concPreset, concCustom);
  const dtpRange = rangeFor(dtpPreset, dtpCustom);
  const compareRange = compareRangeFor(comparePreset, pivotRange, compareCustom);

  // -- Effect: pivot data (+ optional compare) --
  useEffect(() => {
    api.get("/reports/pivot", { params: { group, start: pivotRange.start || undefined, end: pivotRange.end || undefined } })
       .then((r) => setPivot(r.data));
    if (compareOn) {
      api.get("/reports/pivot", { params: { group, start: compareRange.start || undefined, end: compareRange.end || undefined } })
         .then((r) => setPivotCompare(r.data));
    } else {
      setPivotCompare(null);
    }
  }, [group, pivotRange.start, pivotRange.end, compareOn, compareRange.start, compareRange.end]);

  // -- Effect: billed KPI + prior --
  useEffect(() => {
    api.get("/reports/kpi-summary", { params: { start: billedRange.start || undefined, end: billedRange.end || undefined } })
       .then((r) => setBilledKpi(r.data));
    const prior = priorRange(billedRange);
    if (prior) {
      api.get("/reports/kpi-summary", { params: { start: prior.start, end: prior.end, spark_months: 1 } })
         .then((r) => setBilledKpiPrior(r.data));
    } else setBilledKpiPrior(null);
  }, [billedRange.start, billedRange.end]);

  // -- Effect: paid KPI + prior --
  useEffect(() => {
    api.get("/reports/kpi-summary", { params: { start: paidRange.start || undefined, end: paidRange.end || undefined } })
       .then((r) => setPaidKpi(r.data));
    const prior = priorRange(paidRange);
    if (prior) {
      api.get("/reports/kpi-summary", { params: { start: prior.start, end: prior.end, spark_months: 1 } })
         .then((r) => setPaidKpiPrior(r.data));
    } else setPaidKpiPrior(null);
  }, [paidRange.start, paidRange.end]);

  // -- Effect: concentration --
  useEffect(() => {
    api.get("/reports/client-concentration", { params: { start: concRange.start || undefined, end: concRange.end || undefined } })
       .then((r) => setConcentration(r.data));
  }, [concRange.start, concRange.end]);

  // -- Effect: days to pay --
  useEffect(() => {
    api.get("/reports/days-to-pay", { params: { start: dtpRange.start || undefined, end: dtpRange.end || undefined } })
       .then((r) => setDaysToPay(r.data));
  }, [dtpRange.start, dtpRange.end]);

  // -- Effect: utilization + aging (run once) --
  useEffect(() => {
    api.get("/reports/utilization", { params: { months: 3 } }).then((r) => setUtilization(r.data));
    api.get("/reports/receivables-aging").then((r) => setAging(r.data));
    api.get("/reports/saved-views").then((r) => setSavedViews(r.data || []));
    api.get("/reports/cash-flow-forecast", { params: { horizon_days: 90 } }).then((r) => setForecast(r.data));
    api.get("/reports/client-reliability").then((r) => setReliability(r.data));
  }, []);

  // -- Effect: historic trend (refetches when preset changes) --
  useEffect(() => {
    api.get("/reports/historic-trend", { params: { preset: trendPreset } }).then((r) => setTrend(r.data));
  }, [trendPreset]);

  const metricObj = METRICS.find((m) => m.key === metric) || METRICS[0];
  const compareByKey = useMemo(() => {
    if (!compareOn || !pivotCompare?.rows) return {};
    return Object.fromEntries(pivotCompare.rows.map((r) => [r.id, r]));
  }, [compareOn, pivotCompare]);

  const sortedRows = useMemo(() => {
    if (!pivot?.rows) return [];
    return [...pivot.rows].sort((a, b) => Number(b[metric] || 0) - Number(a[metric] || 0));
  }, [pivot, metric]);

  // -- Save view helpers --
  const saveCurrentView = async () => {
    const name = window.prompt("Save this view as… (name)");
    if (!name?.trim()) return;
    try {
      const cfg = {
        billedPreset, paidPreset, concPreset, dtpPreset,
        pivotPreset, group, metric, compareOn, comparePreset,
        billedCustom, paidCustom, concCustom, dtpCustom, pivotCustom, compareCustom,
      };
      const r = await api.post("/reports/saved-views", { name: name.trim(), view_config: cfg });
      setSavedViews((prev) => [r.data, ...prev]);
      toast.success(`Saved view "${name}".`);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };
  const applyView = (v) => {
    const c = v.view_config || {};
    if (c.billedPreset) setBilledPreset(c.billedPreset);
    if (c.paidPreset) setPaidPreset(c.paidPreset);
    if (c.concPreset) setConcPreset(c.concPreset);
    if (c.dtpPreset) setDtpPreset(c.dtpPreset);
    if (c.pivotPreset) setPivotPreset(c.pivotPreset);
    if (c.group) setGroup(c.group);
    if (c.metric) setMetric(c.metric);
    if (typeof c.compareOn === "boolean") setCompareOn(c.compareOn);
    if (c.comparePreset) setComparePreset(c.comparePreset);
    setBilledCustom(c.billedCustom || null);
    setPaidCustom(c.paidCustom || null);
    setConcCustom(c.concCustom || null);
    setDtpCustom(c.dtpCustom || null);
    setPivotCustom(c.pivotCustom || null);
    setCompareCustom(c.compareCustom || null);
    toast.success(`Applied "${v.name}".`);
  };
  const deleteView = async (v) => {
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await api.delete(`/reports/saved-views/${v.id}`);
      setSavedViews((prev) => prev.filter((x) => x.id !== v.id));
      toast.success("Deleted.");
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  // -- CSV export helpers --
  const exportPivotCsv = () => {
    if (!pivot?.rows?.length) return toast.error("Nothing to export.");
    const cols = [
      { key: "name", label: `${GROUPS.find((g) => g.key === group)?.label || "Dim"}` },
      { key: "billed", label: "Billed $" },
      { key: "paid", label: "Paid $" },
      { key: "outstanding", label: "Outstanding $" },
      { key: "bill", label: "TS Bill $" },
      { key: "pay", label: "Worker Pay $" },
      { key: "margin", label: "Margin $" },
      { key: "hours", label: "Hours" },
      { key: "timesheet_count", label: "Timesheet #" },
    ];
    if (compareOn) {
      cols.push({ key: `_cmp_${metric}`, label: `Prior ${metricObj.label}` },
                 { key: `_delta_${metric}`, label: `Δ vs prior ${metricObj.label}` });
    }
    const rows = sortedRows.map((r) => {
      const out = { ...r };
      if (compareOn) {
        const c = compareByKey[r.id];
        out[`_cmp_${metric}`] = c ? c[metric] : 0;
        out[`_delta_${metric}`] = c ? (Number(r[metric] || 0) - Number(c[metric] || 0)).toFixed(2) : "";
      }
      return out;
    });
    downloadCsv(`report-pivot-by-${group}-${new Date().toISOString().slice(0, 10)}.csv`, cols, rows);
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Reporting</div>
          <h1 className="text-3xl font-black">Reports</h1>
          <p className="text-slate-500 mt-1 text-sm">Each card has its own date range. Save any layout as a named view for one-click recall.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <SavedViewsMenu views={savedViews} onApply={applyView} onDelete={deleteView} />
          <button data-testid="save-view-btn" onClick={saveCurrentView}
                   className="h-9 px-3 border border-slate-300 rounded-md text-sm font-semibold bg-white hover:bg-slate-50 inline-flex items-center gap-2">
            <FloppyDisk size={14} weight="bold" /> Save view
          </button>
        </div>
      </div>

      {/* ---- Data Assistant (AI query tool) ---- */}
      <DataAssistant />

      {/* ---- Empty-state onboarding banner (only when there's literally no data) ---- */}
      {billedKpi && paidKpi && aging &&
        Number(billedKpi.total || 0) === 0 &&
        Number(paidKpi.total || 0) === 0 &&
        Number(aging.total_outstanding || 0) === 0 && (
        <div data-testid="reports-empty-state" className="bg-teal-50 border border-teal-200 rounded-md p-5 mb-6">
          <div className="text-lg font-bold text-teal-900">No data yet — reports light up as invoices flow in.</div>
          <div className="text-sm text-teal-800 mt-1">
            Once you import historic invoices, or generate the first invoice from an approved timesheet,
            you'll see billed / paid totals, aging, pivot tables, and every downstream chart populate here.
            <span className="block mt-2">
              <a href="/invoices" className="font-semibold underline">Go to Invoices</a>
              <span className="mx-2 text-teal-400">·</span>
              <a href="/timesheets" className="font-semibold underline">Review Timesheets</a>
            </span>
          </div>
        </div>
      )}

      {/* ---- Cash flow forecast (top of page) ---- */}
      <CashFlowForecastCard forecast={forecast} />

      {/* ---- 3 KPI cards ---- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <BilledPaidCard
          label="Total Billed"
          testid="kpi-billed"
          preset={billedPreset} setPreset={setBilledPreset}
          custom={billedCustom} setCustom={setBilledCustom}
          data={billedKpi} prior={billedKpiPrior}
          metric="billed" tone="#0f766e" fill="#0f766e22"
        />
        <BilledPaidCard
          label="Total Paid"
          testid="kpi-paid"
          preset={paidPreset} setPreset={setPaidPreset}
          custom={paidCustom} setCustom={setPaidCustom}
          data={paidKpi} prior={paidKpiPrior}
          metric="paid" tone="#10b981" fill="#10b98122"
        />
        <OutstandingCard aging={aging} />
      </div>

      {/* ---- Pivot ---- */}
      <section className="bg-white border border-slate-200 rounded-md p-4 mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="tinylabel">Pivot</div>
            <h2 className="text-xl font-bold">Group by dimension</h2>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <SelectField label="Group by" value={group} onChange={setGroup}
                         options={GROUPS} testid="pivot-group-select" />
            <SelectField label="Metric" value={metric} onChange={setMetric}
                         options={METRICS.map((m) => ({ key: m.key, label: m.label }))}
                         testid="pivot-metric-select" />
            <PresetPicker label="Range" testid="pivot-preset" preset={pivotPreset}
                          setPreset={setPivotPreset} custom={pivotCustom} setCustom={setPivotCustom} />
            <label className="flex items-center gap-2 text-sm cursor-pointer h-9 px-3 border border-slate-300 rounded-md bg-white hover:bg-slate-50">
              <input type="checkbox" checked={compareOn} onChange={(e) => setCompareOn(e.target.checked)}
                     data-testid="compare-toggle" className="h-4 w-4 accent-teal-600" />
              Compare period
            </label>
            {compareOn && (
              <PresetPicker label="vs." testid="compare-preset" preset={comparePreset}
                            setPreset={setComparePreset} custom={compareCustom} setCustom={setCompareCustom}
                            presets={COMPARE_PRESETS} />
            )}
            <button data-testid="export-pivot-btn" onClick={exportPivotCsv}
                     className="h-9 px-3 border border-slate-300 rounded-md text-sm font-semibold bg-white hover:bg-slate-50 inline-flex items-center gap-2">
              <Download size={14} weight="bold" /> CSV
            </button>
          </div>
        </div>

        {!pivot && <div className="p-6 text-slate-400 text-center">Loading…</div>}
        {pivot && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">{GROUPS.find((g) => g.key === group)?.label}</th>
                  <th className="text-right px-4 py-3">Billed</th>
                  <th className="text-right px-4 py-3">Paid</th>
                  <th className="text-right px-4 py-3">Outstanding</th>
                  <th className="text-right px-4 py-3">Bill (TS)</th>
                  <th className="text-right px-4 py-3">Pay</th>
                  <th className="text-right px-4 py-3">Margin</th>
                  <th className="text-right px-4 py-3">Hrs</th>
                  {compareOn && <th className="text-right px-4 py-3">Δ {metricObj.label}</th>}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 && <tr><td colSpan={compareOn ? 9 : 8} className="px-4 py-8 text-center text-slate-400">No data in this range.</td></tr>}
                {sortedRows.map((r) => {
                  const drillable = r.id && !r.id.startsWith("pname::") && r.id !== "_unknown";
                  const cmpRow = compareByKey[r.id];
                  const cmpVal = cmpRow ? Number(cmpRow[metric] || 0) : 0;
                  const curVal = Number(r[metric] || 0);
                  const delta = curVal - cmpVal;
                  const pct = cmpVal ? (delta / Math.abs(cmpVal)) * 100 : (curVal > 0 ? 100 : 0);
                  return (
                    <tr key={r.id} className={`border-t border-slate-100 ${drillable ? "hover:bg-slate-50 cursor-pointer" : ""}`}
                        data-testid={`pivot-row-${r.id}`}
                        onClick={() => { if (drillable) setDrill({ type: group, id: r.id, name: r.name }); }}>
                      <td className="px-4 py-3 font-medium">
                        {r.name}
                        {drillable && <span className="ml-2 text-xs text-teal-700">↗</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{money(r.billed)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{money(r.paid)}</td>
                      <td className={`px-4 py-3 text-right font-mono ${r.outstanding > 0 ? "text-rose-600 font-bold" : "text-slate-400"}`}>{money(r.outstanding)}</td>
                      <td className="px-4 py-3 text-right font-mono">{money(r.bill)}</td>
                      <td className="px-4 py-3 text-right font-mono">{money(r.pay)}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{money(r.margin)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.hours.toLocaleString()}</td>
                      {compareOn && (
                        <td className={`px-4 py-3 text-right font-mono text-xs ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-400"}`}>
                          {delta === 0 ? "—" : (
                            <span className="inline-flex items-center gap-1">
                              {delta > 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                              {delta > 0 ? "+" : ""}{metricObj.fmt(delta)} <span className="text-slate-500">({pct > 0 ? "+" : ""}{pct.toFixed(0)}%)</span>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---- Concentration + Days-to-pay ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="tinylabel">Revenue mix</div>
              <h2 className="text-xl font-bold">Client concentration</h2>
            </div>
            <PresetPicker label="" testid="conc-preset" preset={concPreset}
                          setPreset={setConcPreset} custom={concCustom} setCustom={setConcCustom} />
          </div>
          <p className="text-xs text-slate-500 mb-2">Share of total billed $ by client — spot over-reliance.</p>
          {concentration && concentration.rows?.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={concentration.rows} dataKey="billed" nameKey="client_name"
                          innerRadius={40} outerRadius={80} paddingAngle={2}>
                      {concentration.rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5">
                {concentration.rows.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="flex-1 truncate">{r.client_name}</span>
                    <span className="font-mono text-slate-500 text-xs">{r.share}%</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : <div className="p-6 text-slate-400 text-center text-sm">No invoice data in range.</div>}
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="tinylabel">Collections</div>
              <h2 className="text-xl font-bold">Average days to pay</h2>
            </div>
            <PresetPicker label="" testid="dtp-preset" preset={dtpPreset}
                          setPreset={setDtpPreset} custom={dtpCustom} setCustom={setDtpCustom} />
          </div>
          <p className="text-xs text-slate-500 mb-3">Invoice sent → fully paid, averaged per client.</p>
          {daysToPay?.rows?.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="text-left px-3 py-2">Client</th><th className="text-right px-3 py-2">Avg days</th><th className="text-right px-3 py-2">Worst</th><th className="text-right px-3 py-2">Inv #</th></tr>
              </thead>
              <tbody>
                {daysToPay.rows.map((r) => (
                  <tr key={r.client_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                       onClick={() => setDrill({ type: "client", id: r.client_id, name: r.client_name })}>
                    <td className="px-3 py-2 font-medium">{r.client_name} <span className="text-xs text-teal-700">↗</span></td>
                    <td className={`px-3 py-2 text-right font-mono ${r.avg_days > 30 ? "text-rose-600 font-bold" : ""}`}>{r.avg_days}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.worst}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.invoice_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="p-6 text-slate-400 text-center text-sm">No paid invoices in range.</div>}
        </section>
      </div>

      {/* ---- Client payment reliability ---- */}
      <ClientReliabilitySection reliability={reliability} onDrill={setDrill} />

      {/* ---- Utilization + Trend ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-white border border-slate-200 rounded-md p-4">
          <div className="tinylabel">Activity</div>
          <h2 className="text-xl font-bold mb-1">Worker utilization</h2>
          <p className="text-xs text-slate-500 mb-3">Hours per worker over the last ~3 months.</p>
          {utilization?.rows?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Worker</th>
                    <th className="text-right px-3 py-2">Total hrs</th>
                    <th className="text-right px-3 py-2">Weeks active</th>
                    <th className="text-right px-3 py-2">Avg / active wk</th>
                  </tr>
                </thead>
                <tbody>
                  {utilization.rows.slice(0, 10).map((r) => (
                    <tr key={r.worker_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                         onClick={() => setDrill({ type: "worker", id: r.worker_id, name: r.worker })}>
                      <td className="px-3 py-2 font-medium">{r.worker} <span className="text-xs text-teal-700">↗</span></td>
                      <td className="px-3 py-2 text-right font-mono">{r.total_hours}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.active_weeks}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.avg_weekly_hours}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="p-6 text-slate-400 text-center text-sm">No approved timesheets in the last 3 months.</div>}
        </section>

        <section className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <div className="tinylabel">Trend</div>
              <h2 className="text-xl font-bold">Historic billing trend</h2>
              <p className="text-xs text-slate-500 mb-1">
                Billed vs paid over your selected timeframe.
                {trend?.granularity === "day" ? " Daily buckets." : trend?.granularity === "week" ? " Weekly buckets." : " Monthly buckets."}
              </p>
            </div>
            <PresetPicker label="Range" testid="trend-preset" preset={trendPreset}
                          setPreset={setTrendPreset} custom={null} setCustom={() => {}}
                          presets={TREND_PRESETS} />
          </div>
          {trend?.rows?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }}
                    tickFormatter={(v) => {
                      if (!v || v.length < 10) return v;
                      const [, m, d] = v.split("-");
                      return `${m}/${d}`;
                    }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={short} />
                  <Tooltip formatter={(v) => money(v)}
                    labelFormatter={(v) => trend?.granularity === "week" ? `Wk of ${v}` : v} />
                  <Legend />
                  <Line type="monotone" dataKey="billed" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="Billed" />
                  <Line type="monotone" dataKey="paid" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} name="Paid" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="p-6 text-slate-400 text-center text-sm">No invoice data in this range.</div>}
        </section>
      </div>

      {drill && (
        <ReportDrillInModal
          type={drill.type} id={drill.id} name={drill.name}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function BilledPaidCard({ label, testid, preset, setPreset, custom, setCustom, data, prior, metric, tone, fill }) {
  const current = Number(data?.[metric] || 0);
  const priorVal = Number(prior?.[metric] || 0);
  const delta = current - priorVal;
  const pct = priorVal ? (delta / Math.abs(priorVal)) * 100 : (current > 0 ? 100 : 0);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="tinylabel">{label}</div>
        <PresetPicker label="" testid={`${testid}-preset`} preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} />
      </div>
      <div className={`text-3xl font-black ${metric === "paid" ? "text-emerald-600" : "text-slate-900"}`}>
        {data ? money(current) : "…"}
      </div>
      <div className="mt-1 flex items-center gap-3">
        {prior && (
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-500"}`}>
            {delta > 0 ? <TrendUp size={12} weight="bold" /> : delta < 0 ? <TrendDown size={12} weight="bold" /> : null}
            {delta > 0 ? "+" : ""}{pct.toFixed(0)}%
            <span className="text-slate-400 font-normal">vs. prior</span>
          </span>
        )}
        <div className="ml-auto">
          <Sparkline data={data ? data[`spark_${metric}`] || [] : []} width={110} height={28}
                     stroke={tone} fill={fill} />
        </div>
      </div>
      <div className="mt-1 text-xs text-slate-400">
        {data?.invoice_count ? `${data.invoice_count} invoice${data.invoice_count === 1 ? "" : "s"} in range` : "No invoices in range"}
      </div>
    </div>
  );
}

function OutstandingCard({ aging }) {
  const total = aging ? Number(aging.total_outstanding || 0) : 0;
  const buckets = aging?.buckets || {};
  const pct = (v) => (total > 0 ? (100 * Number(v || 0) / total) : 0);
  const segs = [
    { key: "under_30", label: "Current / <30d", color: "#10b981", val: buckets.under_30 },
    { key: "30_60", label: "31-60d", color: "#f59e0b", val: buckets["30_60"] },
    { key: "60_90", label: "61-90d", color: "#ef4444", val: buckets["60_90"] },
    { key: "over_90", label: "90+d", color: "#7f1d1d", val: buckets.over_90 },
    { key: "undated", label: "Undated", color: "#64748b", val: buckets.undated },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="kpi-outstanding">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="tinylabel">Outstanding</div>
        <span className="text-[10px] text-slate-400">live · all unpaid</span>
      </div>
      <div className="text-3xl font-black text-rose-600">{aging ? money(total) : "…"}</div>
      <div className="mt-2">
        <div className="w-full h-3 rounded-full overflow-hidden bg-slate-100 flex">
          {total > 0 ? segs.map((s) => (
            <div key={s.key} style={{ width: `${pct(s.val)}%`, background: s.color }} title={`${s.label} · ${money(s.val)}`} />
          )) : null}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
          {segs.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
              <span className="text-slate-500">{s.label}</span>
              <span className="font-mono text-slate-800">{pct(s.val).toFixed(0)}%</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PresetPicker({ label, preset, setPreset, custom, setCustom, testid, presets }) {
  const list = presets || PRESETS;
  const [showCustom, setShowCustom] = useState(preset === "custom");
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>}
      <select value={preset} data-testid={testid}
               onChange={(e) => { setPreset(e.target.value); setShowCustom(e.target.value === "custom"); }}
               className="h-8 px-2 border border-slate-300 rounded-md text-xs bg-white font-semibold">
        {list.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {showCustom && (
        <span className="flex items-center gap-1">
          <input type="date" value={custom?.start || ""} onChange={(e) => setCustom({ ...(custom || {}), start: e.target.value })}
                  className="h-8 px-2 border border-slate-300 rounded-md text-xs" />
          <input type="date" value={custom?.end || ""} onChange={(e) => setCustom({ ...(custom || {}), end: e.target.value })}
                  className="h-8 px-2 border border-slate-300 rounded-md text-xs" />
        </span>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
               className="h-9 px-2 border border-slate-300 rounded-md text-sm min-w-[9rem] bg-white">
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SavedViewsMenu({ views, onApply, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="saved-views-btn" onClick={() => setOpen(!open)}
               className="h-9 px-3 border border-slate-300 rounded-md text-sm font-semibold bg-white hover:bg-slate-50 inline-flex items-center gap-2">
        <BookmarkSimple size={14} weight="bold" /> Saved views ({views.length}) <CaretDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 w-72 bg-white border border-slate-200 rounded-md shadow-lg py-1">
          {views.length === 0 && <div className="px-4 py-3 text-sm text-slate-500">No saved views yet. Click <b>Save view</b> to remember your current filters.</div>}
          {views.map((v) => (
            <div key={v.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
              <button onClick={() => { onApply(v); setOpen(false); }}
                       className="flex-1 text-left text-sm font-medium hover:text-teal-700 truncate">
                {v.name}
              </button>
              <button onClick={() => onDelete(v)} className="text-slate-400 hover:text-rose-600" title="Delete">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------- Cash flow forecast (top of page) ----------

function CashFlowForecastCard({ forecast }) {
  const has = forecast && forecast.unpaid_count > 0;
  return (
    <section className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-md p-5 mb-6" data-testid="cash-flow-forecast">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="tinylabel text-teal-700">Forecast</div>
          <h2 className="text-2xl font-black">Cash flow — next 90 days</h2>
          <p className="text-xs text-slate-600 mt-1">
            Projected receipts based on each client's own historic days-to-pay. Falls back to invoice due-date for clients with no paid history yet.
          </p>
        </div>
        {forecast && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Outstanding</div>
            <div className="text-2xl font-black text-slate-900">{money(forecast.total_outstanding)}</div>
            <div className="text-[11px] text-slate-500">{forecast.unpaid_count} unpaid invoice{forecast.unpaid_count === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <ForecastBucket label="Next 30 days" val={forecast?.expected_30d} bg="bg-white" testid="forecast-30d" />
        <ForecastBucket label="Next 60 days" val={forecast?.expected_60d} bg="bg-white" testid="forecast-60d" />
        <ForecastBucket label="Next 90 days" val={forecast?.expected_90d} bg="bg-white" testid="forecast-90d" />
      </div>
      {has && forecast?.weeks?.length ? (
        <div className="h-40 bg-white rounded-md p-2 border border-teal-100">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={forecast.weeks}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week_start" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={short} />
              <Tooltip formatter={(v) => money(v)} labelFormatter={(v) => `Week of ${v}`} />
              <Line type="monotone" dataKey="amount" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="Expected" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-white rounded-md p-6 text-center text-slate-400 text-sm border border-teal-100">
          {forecast ? "No outstanding invoices — you're all caught up." : "Loading…"}
        </div>
      )}
      {forecast && forecast.no_history_client_count > 0 && (
        <div className="mt-2 text-[11px] text-slate-500">
          <Warning size={11} weight="bold" className="inline mb-0.5 mr-1 text-amber-600" />
          {forecast.no_history_client_count} invoice{forecast.no_history_client_count === 1 ? "" : "s"} predicted from due-date (client has no paid history yet).
        </div>
      )}
    </section>
  );
}

function ForecastBucket({ label, val, bg, testid }) {
  return (
    <div className={`${bg} border border-teal-100 rounded-md p-3`} data-testid={testid}>
      <div className="tinylabel">{label}</div>
      <div className="text-2xl font-black text-teal-800 mt-1">{val === undefined || val === null ? "…" : money(val)}</div>
    </div>
  );
}

// ---------- Client reliability (A/B/C/D grades) ----------

const GRADE_STYLE = {
  A: { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-300" },
  B: { bg: "bg-teal-100", text: "text-teal-700", ring: "ring-teal-300" },
  C: { bg: "bg-amber-100", text: "text-amber-700", ring: "ring-amber-300" },
  D: { bg: "bg-rose-100", text: "text-rose-700", ring: "ring-rose-300" },
  "—": { bg: "bg-slate-100", text: "text-slate-500", ring: "ring-slate-300" },
};

function ClientReliabilitySection({ reliability, onDrill }) {
  const rows = reliability?.rows || [];
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="client-reliability">
      <div className="mb-3">
        <div className="tinylabel">Risk</div>
        <h2 className="text-xl font-bold">Client payment reliability</h2>
        <p className="text-xs text-slate-500 mt-1">A/B/C/D grades from historic pay-lag, aged invoices, and reminder count. Click any row to drill in.</p>
      </div>
      {reliability === null ? (
        <div className="p-6 text-slate-400 text-center text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-slate-400 text-center text-sm">No clients with invoice history yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-16">Grade</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-right px-3 py-2">Avg days</th>
                <th className="text-right px-3 py-2">Paid #</th>
                <th className="text-right px-3 py-2">Past-90</th>
                <th className="text-right px-3 py-2">Outstanding $</th>
                <th className="text-left px-3 py-2">Signal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const g = GRADE_STYLE[r.grade] || GRADE_STYLE["—"];
                return (
                  <tr key={r.client_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                       data-testid={`reliability-row-${r.client_id}`}
                       onClick={() => onDrill({ type: "client", id: r.client_id, name: r.client_name })}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-black text-lg ring-2 ${g.bg} ${g.text} ${g.ring}`}
                            title={r.grade_reason}>
                        {r.grade}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {r.client_name} <span className="text-xs text-teal-700">↗</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.avg_days_to_pay ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.paid_count}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.past_90_count > 0 ? "text-rose-600 font-bold" : "text-slate-500"}`}>
                      {r.past_90_count}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${r.outstanding_total > 0 ? "text-rose-600" : "text-slate-400"}`}>
                      {r.outstanding_total > 0 ? money(r.outstanding_total) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.grade_reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
